import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/lib/articles";

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const navRef = useRef<HTMLDivElement>(null);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setSearchOpen(false);
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-background-50 border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading text-lg font-bold">
              鲁
            </span>
            <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
              鲁啊鲁
            </span>
          </Link>

          <nav ref={navRef} className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive("/")
                  ? "text-primary-600"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              首页
            </Link>
            {allCategories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive(`/category/${category.id}`)
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {category.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive("/about")
                  ? "text-primary-600"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive("/contact")
                  ? "text-primary-600"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              联系我们
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden md:flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 focus-within:border-primary-400 transition-colors"
            >
              <i className="ri-search-line text-foreground-500 text-sm w-4 h-4 flex items-center justify-center"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索资讯"
                className="w-36 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                aria-label="搜索资讯"
              />
              <button
                type="submit"
                className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              className="flex md:hidden h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex lg:hidden h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
              aria-label="菜单"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="md:hidden pb-3 flex items-center gap-2">
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索资讯关键词"
              className="flex-1 rounded-full border border-background-300 bg-background-100 px-4 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              aria-label="搜索资讯"
            />
            <button
              type="submit"
              className="rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        )}
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              首页
            </Link>
            {allCategories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {category.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              联系我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}